import { z } from "zod";

import { unlistedDiscriminantSchema } from "../../../shared/core/discriminated-union-read.schema";
import {
  VibeBuildExecutorReadSchema,
  VibeBuildExecutorSchema,
  VibeOriginLockReadSchema,
  VibeOriginLockSchema
} from "./zadmin-vibe-tenant-cluster-list-item";
import { AdminVibeTenantClusterForceConvergeOutcomeReadSchema } from "./zadmin-vibe-tenant-cluster-operator-repair";

// ============================================================
// Set the ingress origin lock
// ============================================================

/**
 * Move ONE tenant's ingress origin lock one step: OPEN ⇄ SPLIT ⇄ LOCKED.
 *
 * One step because each step is what makes the next one safe: SPLIT moves the
 * grey-cloud git and logs hostnames to a direct ALB while the app ALB still
 * serves them, and only once their cached CNAMEs have expired may LOCKED close
 * the app ALB to everything but Cloudflare and the custom-domain edge. A jump
 * refuses a git client still holding the old CNAME
 * (apps/vibe-provisioner/ORIGIN-LOCK-ROLLOUT.md).
 */
export const AdminVibeTenantClusterSetOriginLockBodySchema = z
  .object({
    organizationId: z.string().min(1),
    mode: VibeOriginLockSchema,
    /**
     * Why. Required: it becomes the forced converge's `statusReason`, which is
     * what the next person to see this cluster DEGRADED reads.
     */
    reason: z.string().min(1).max(500)
  })
  .strict();

/**
 * Discriminated outcome. Every variant is a true answer; only a persistence
 * fault is an error.
 *
 *   - set: the mode moved one step and a converge was asked for. `converge` is
 *     the force-converge answer verbatim — `reconcile_paused` there means the
 *     new mode waits for the pause to be released.
 *   - unchanged: the tenant already runs that mode. Nothing written. A never-set
 *     tenant asked for OPEN is this, and stays unset.
 *   - skips_a_step: refused — OPEN ⇄ LOCKED in one move skips the canary step.
 *   - conflict: another write moved the mode between this call's read and its
 *     write. Nothing written; read the roster and decide again.
 */
export const AdminVibeTenantClusterSetOriginLockOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("set"),
    from: VibeOriginLockReadSchema.nullable(),
    to: VibeOriginLockReadSchema,
    converge: AdminVibeTenantClusterForceConvergeOutcomeReadSchema
  }),
  z.object({ kind: z.literal("unchanged"), mode: VibeOriginLockReadSchema.nullable() }),
  z.object({
    kind: z.literal("skips_a_step"),
    from: VibeOriginLockReadSchema.nullable(),
    to: VibeOriginLockReadSchema
  }),
  z.object({ kind: z.literal("conflict") }),
  z.object({ kind: z.literal("not_found") })
]);

/** `AdminVibeTenantClusterSetOriginLockOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
export const AdminVibeTenantClusterSetOriginLockOutcomeReadSchema = z.union([
  AdminVibeTenantClusterSetOriginLockOutcomeSchema,
  z.looseObject({
    kind: unlistedDiscriminantSchema(AdminVibeTenantClusterSetOriginLockOutcomeSchema)
  })
]);

// ============================================================
// Switch the build executor
// ============================================================

/**
 * Switch ONE tenant's app builds between Nomad and CodeBuild — the audited path
 * for a cut-over and for its rollback.
 *
 * Either direction is accepted from any state; what differs is what it takes:
 *
 * - → CODEBUILD forces a converge. The `build` stack is applied only for a tenant
 *   already switched, so the project cannot exist before the switch, and until
 *   that converge applies it new builds keep starting on Nomad — `startOn` in the
 *   outcome says which is true right now.
 * - → NOMAD needs no converge. New builds start on Nomad at once; the `build`
 *   stack stays applied and the agent still finishes, relays and retires the
 *   CodeBuild builds already running.
 */
export const AdminVibeTenantClusterSetBuildExecutorBodySchema = z
  .object({
    organizationId: z.string().min(1),
    executor: VibeBuildExecutorSchema,
    /**
     * Why. Required: it is the operator's stated reason on the audit row, and on
     * a move to CODEBUILD it becomes the forced converge's `statusReason`, which
     * is what the next person to see this cluster DEGRADED reads.
     */
    reason: z.string().min(1).max(500)
  })
  .strict();

/**
 * Discriminated outcome. Every variant is a true answer; only a persistence
 * fault is an error.
 *
 *   - set: the executor moved. `startOn` is where the agent is now told to start
 *     new builds — `NOMAD` after a move to CODEBUILD means the project is not
 *     known yet and CodeBuild takes over once the forced converge applies the
 *     `build` stack. `converge` is the force-converge answer verbatim
 *     (`reconcile_paused` there means it waits for the pause to be released), or
 *     null for a move to NOMAD, which needs none.
 *   - unchanged: the tenant already builds there. Nothing written, nothing
 *     converged — a repeated request is this.
 *   - conflict: another write moved the executor between this call's read and its
 *     write. Nothing written; read the roster and decide again.
 */
export const AdminVibeTenantClusterSetBuildExecutorOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("set"),
    from: VibeBuildExecutorReadSchema,
    to: VibeBuildExecutorReadSchema,
    startOn: VibeBuildExecutorReadSchema,
    converge: AdminVibeTenantClusterForceConvergeOutcomeReadSchema.nullable()
  }),
  z.object({ kind: z.literal("unchanged"), executor: VibeBuildExecutorReadSchema }),
  z.object({ kind: z.literal("conflict") }),
  z.object({ kind: z.literal("not_found") })
]);

/** `AdminVibeTenantClusterSetBuildExecutorOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
export const AdminVibeTenantClusterSetBuildExecutorOutcomeReadSchema = z.union([
  AdminVibeTenantClusterSetBuildExecutorOutcomeSchema,
  z.looseObject({
    kind: unlistedDiscriminantSchema(AdminVibeTenantClusterSetBuildExecutorOutcomeSchema)
  })
]);

export type AdminVibeTenantClusterSetOriginLockBody = z.infer<
  typeof AdminVibeTenantClusterSetOriginLockBodySchema
>;
export type AdminVibeTenantClusterSetOriginLockOutcome = z.infer<
  typeof AdminVibeTenantClusterSetOriginLockOutcomeSchema
>;
export type AdminVibeTenantClusterSetBuildExecutorBody = z.infer<
  typeof AdminVibeTenantClusterSetBuildExecutorBodySchema
>;
export type AdminVibeTenantClusterSetBuildExecutorOutcome = z.infer<
  typeof AdminVibeTenantClusterSetBuildExecutorOutcomeSchema
>;
